<template>
  <div class="loader-container">
    <div
      class="loader"
      :class="{ 'fade-out': isResetting }"
      :style="{ width: `${progress}%` }"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, onUnmounted } from 'vue';

const progress = ref(0);
const isResetting = ref(false);
let animationFrameId: number | null = null;
let startTime: number | null = null;

const animate = (duration: number, targetProgress: number, startFrom : number, onComplete?: () => void) => {
  if (startTime === null) {
    startTime = performance.now();
    progress.value = startFrom;
  }

  const currentTime = performance.now();
  const elapsed = currentTime - startTime;
  const progressPercentage = Math.min(elapsed / duration, 1);

  progress.value = startFrom + ((targetProgress - startFrom) * progressPercentage);

  if (progressPercentage < 1) {
    animationFrameId = requestAnimationFrame(() =>
      animate(duration, targetProgress, startFrom, onComplete));
  } else {
    startTime = null;
    if (onComplete) {
      onComplete();
    }
  }
};

const resetLoader = async () => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
  }
  isResetting.value = true;

  // eslint-disable-next-line no-promise-executor-return
  await new Promise(resolve => setTimeout(resolve, 300));
  progress.value = 0;
  startTime = null;
  // eslint-disable-next-line no-promise-executor-return
  await new Promise(resolve => setTimeout(resolve, 50));
  isResetting.value = false;
};

defineExpose({
  animate,
  resetLoader,
});

onUnmounted(() => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
  }
});
</script>

<style lang="scss" scoped>
.loader-container {
  width: 100%;
  height: 16px;
  background-color: #F6F8ED;
  overflow: hidden;
  border-radius: 8px;
}

.loader {
  width: 0;
  height: 100%;
  background-color: #13C636;
  border-radius: 8px;
  opacity: 1;
}

.fade-out {
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}
</style>
